import type { ApiResponse } from '@pbk-web/data';
import { Api } from '@pbk-web/data';
import type { FeatureLimit, PagingFilter, UserWithTags } from '@pbk-web/types';

import type {
  ClientTrainingEventUsersResponse,
  Training,
  TrainingCoverageResponse,
  TrainingCreate,
  TrainingMemberResponse,
  TrainingRequirementsResponse,
  TrainingStats,
  UpsertTrainingRequirementsRequest,
} from '../models/Training';
import type { TrainingAccessResponse } from '../models/TrainingAssignment';
import type { TrainingEvent, TrainingEventsPagedFilter, TrainingEventUpsertDto, TrainingEventUserRecord } from '../models/TrainingEvent';
import type { TrainingGapAnalysisFilter, TrainingGapAnalysisResponse } from '../models/TrainingGapAnalysis';
import type {
  TrainingMaterial,
  TrainingMaterialFilter,
  TrainingMaterialGroupedResponse,
  TrainingMaterialRequest,
  TrainingMaterialResponse,
} from '../models/TrainingMaterial';
import type { TrainingMatrixFilter, TrainingMatrixResponse } from '../models/TrainingMatrix';
import type { TrainingQuizResponse, TrainingQuizzesFilter } from '../models/TrainingQuiz';
import type {
  ClientFormReportRow,
  ProgramReportRow,
  TrainingReportByClientFormFilter,
  TrainingReportByProgramFilter,
} from '../models/TrainingReport';

class TrainingService extends Api {
  public static getTraining(id: string): Promise<ApiResponse<Training>> {
    return this.get(`/v1/training/${id}`);
  }

  // Ids of the programs the current user is a trainer on, scoped to the request's client header.
  public static getMyTrainings(): Promise<ApiResponse<string[]>> {
    return this.get(`/v1/my/trainings`);
  }

  public static getTrainingPaged(filter: { originId?: string; clientFormIds?: string[] } & PagingFilter): Promise<ApiResponse<Training[]>> {
    return this.get(`/v1/training/paged`, { params: filter });
  }

  public static createTraining(training: TrainingCreate): Promise<ApiResponse<Training>> {
    return this.post(`/v1/training`, training);
  }

  public static updateTraining(id: string, training: TrainingCreate): Promise<ApiResponse<Training>> {
    return this.put(`/v1/training/${id}`, training);
  }

  // ─── Policy-based member assignment (read) ────────────────────────────────────
  // Members are SAVED via the `access` field on training create/update (one call together with the
  // rest of the program). This read endpoint returns saved policies + server-resolved members.
  public static getTrainingAccess(id: string): Promise<ApiResponse<TrainingAccessResponse>> {
    return this.get(`/v1/training/${id}/access`);
  }

  // Event members are SAVED via the `access` field on event create/update (mirrors the program flow).
  // This read endpoint returns the event's saved policies + server-resolved members.
  public static getEventAccess(clientTrainingId: string, eventId: string): Promise<ApiResponse<TrainingAccessResponse>> {
    return this.get(`/v1/training/${clientTrainingId}/events/${eventId}/access`);
  }

  public static getUnmetUsers(trainingId: string, clientFormIds: string[]): Promise<ApiResponse<ClientTrainingEventUsersResponse>> {
    return this.post(`/v1/training/${trainingId}/unmet-users`, { clientFormIds });
  }

  public static createEvent(trainingEvent: TrainingEventUpsertDto): Promise<ApiResponse<TrainingEvent>> {
    return this.post(`/v1/training/${trainingEvent.clientTrainingId}/events`, trainingEvent);
  }

  public static updateEvent(trainingEvent: TrainingEventUpsertDto): Promise<ApiResponse<TrainingEvent>> {
    return this.put(`/v1/training/${trainingEvent.clientTrainingId}/events/${trainingEvent.id}`, trainingEvent);
  }

  public static createTrainingDefaultMaterial(
    trainingId: string,
    request: TrainingMaterialRequest,
  ): Promise<ApiResponse<TrainingMaterialResponse[]>> {
    return this.post(`/v1/training/${trainingId}/default-materials`, request);
  }

  public static getTrainingDefaultMaterial(trainingId: string, filter: TrainingMaterialFilter): Promise<ApiResponse<TrainingMaterial[]>> {
    return this.get(`/v1/training/${trainingId}/default-materials`, { params: filter });
  }

  public static getTrainingMaterial(trainingId: string, filter: TrainingMaterialFilter): Promise<ApiResponse<TrainingMaterialResponse[]>> {
    return this.get(`/v1/training/${trainingId}/materials`, { params: filter });
  }

  public static getTrainingMaterialGrouped(
    trainingId: string,
    filter: TrainingMaterialFilter,
  ): Promise<ApiResponse<TrainingMaterialGroupedResponse[]>> {
    return this.get(`/v1/training/${trainingId}/materials/grouped`, { params: filter });
  }

  public static getMaterialAcknowledgement(trainingId: string, trainingEventId: string): Promise<ApiResponse<string[]>> {
    return this.get(`/v1/training/${trainingId}/events/${trainingEventId}/materials/acknowledged`);
  }

  public static acknowledgeMaterial(
    trainingId: string,
    trainingEventId: string,
    materialId: string,
    acknowledge: boolean,
  ): Promise<ApiResponse<boolean>> {
    return this.post(`/v1/training/${trainingId}/events/${trainingEventId}/materials/${materialId}/acknowledge`, { acknowledge: acknowledge });
  }

  public static getTrainingEventsPaged(filter: TrainingEventsPagedFilter): Promise<ApiResponse<TrainingEvent[]>> {
    return this.get('/v1/training/training-events-paged', { params: filter });
  }

  public static getCurrentTrainingEventUser(trainingEventUsers: TrainingMemberResponse[], currentUser: UserWithTags): TrainingMemberResponse | null {
    return trainingEventUsers.find((user) => user.userId === currentUser.id) || null;
  }

  public static getTrainingQuizzes(trainingId: string, filter: TrainingQuizzesFilter): Promise<ApiResponse<TrainingQuizResponse[]>> {
    return this.get(`/v1/training/${trainingId}/quizzes`, { params: filter });
  }

  public static getRecords(
    trainingId: string,
    latestOnly: boolean,
    trainingEventId?: string,
    pageSize?: number,
    pageNumber?: number,
  ): Promise<ApiResponse<TrainingEventUserRecord[]>> {
    return this.get(`/v1/training/${trainingId}/trainee-records`, { params: { trainingEventId, latestOnly, pageSize, pageNumber } });
  }

  public static getTrainingStats(trainingId: string, eventIds?: string[]): Promise<ApiResponse<TrainingStats>> {
    return this.get(`/v1/training/${trainingId}/stats`, { params: { eventIds } });
  }

  public static getTrainingCoverage(formId: string, version?: number): Promise<ApiResponse<TrainingCoverageResponse>> {
    return this.get(`/v1/forms/${formId}/training-coverage`, { params: { version } });
  }

  public static getTrainingRequirements(formId: string): Promise<ApiResponse<TrainingRequirementsResponse>> {
    return this.get(`/v1/forms/${formId}/training-requirements`);
  }

  public static upsertTrainingRequirements(formId: string, request: UpsertTrainingRequirementsRequest): Promise<ApiResponse<void>> {
    return this.put(`/v1/forms/${formId}/training-requirements`, request);
  }

  public static getGapAnalysisReport(filter: TrainingGapAnalysisFilter): Promise<ApiResponse<TrainingGapAnalysisResponse[]>> {
    return this.get('/v1/training/reports/gap', { params: filter });
  }

  public static exportGapAnalysisReport(
    filter: Omit<TrainingGapAnalysisFilter, 'pageSize' | 'pageNumber' | 'sortBy'>,
  ): Promise<ApiResponse<{ jobId: string }>> {
    return this.post('/v1/training/reports/gap/export', filter);
  }

  public static getMatrixReport(filter: TrainingMatrixFilter): Promise<ApiResponse<TrainingMatrixResponse>> {
    return this.get('/v1/training/reports/matrix', { params: filter });
  }

  public static archiveTrainingProgram(trainingId: string): Promise<ApiResponse<void>> {
    return this.post(`/v1/training/${trainingId}/archive`);
  }

  public static restoreTrainingProgram(trainingId: string): Promise<ApiResponse<void>> {
    return this.post(`/v1/training/${trainingId}/restore`);
  }

  public static getReportByProgram(filter: TrainingReportByProgramFilter): Promise<ApiResponse<ProgramReportRow[]>> {
    return this.get('/v1/training/reports/by-program', { params: filter });
  }

  public static getReportByClientForm(filter: TrainingReportByClientFormFilter): Promise<ApiResponse<ClientFormReportRow[]>> {
    return this.get('/v1/training/reports/by-client-form', { params: filter });
  }

  public static getTrainingLimit(): Promise<ApiResponse<FeatureLimit>> {
    return this.get('/v1/training/limits');
  }
}

export default TrainingService;
